import { COOKIE } from "@/constants/Cookie";
import { getClientCookies } from "@/lib/cookies";
import type { GetUserByIdApiResponse, SignInSuccess } from "@/services/schemas";
import type { Nullable } from "@/types/Nullable";
import { isSessionExpired } from "@/utils/cookieExpiry";
import { isAdmin, isResponsible } from "@/utils/permissions";
import { deleteCookie } from "cookies-next";
import { produce } from "immer";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type UserStoreProps = Nullable<Omit<SignInSuccess, "token">> & {
  isAdmin: boolean;
  isResponsible: boolean;
};

const initialState: UserStoreProps = {
  id: null,
  firstname: null,
  lastname: null,
  fullname: null,
  initials: null,
  activationToken: null,
  status: null,
  email: null,
  role: null,
  roles: null,
  type: null,
  attributions: null,
  isAdmin: false,
  isResponsible: false,
};

type Actions = {
  reset: () => void;
  storeLogin: (props: SignInSuccess) => void;
  updateAccount: (
    props: Pick<GetUserByIdApiResponse, "firstname" | "lastname">,
  ) => void;
  updateStatus: (status: GetUserByIdApiResponse["status"]) => void;
};

export const useUserStore = create(
  persist<UserStoreProps & Actions>(
    (set) => ({
      ...initialState,
      storeLogin: (props: SignInSuccess) =>
        set({
          ...props,
          isAdmin: isAdmin(props.attributions),
          isResponsible: isResponsible(props.role, props.attributions),
        }),
      updateAccount: (
        props: Pick<GetUserByIdApiResponse, "firstname" | "lastname">,
      ) =>
        set((store) =>
          produce(store, (draft) => {
            draft.firstname = props.firstname;
            draft.lastname = props.lastname;
            draft.fullname = `${props.firstname} ${props.lastname}`;
            draft.initials =
              `${props.firstname[0]}${props.lastname[0]}`.toUpperCase();
          }),
        ),
      updateStatus: (status: GetUserByIdApiResponse["status"]) =>
        set((store) =>
          produce(store, (draft) => {
            draft.status = status;
          }),
        ),
      reset: () => {
        deleteCookie(COOKIE.NAME);
        set(initialState);
      },
    }),
    {
      name: "raip-user-store:v2.1.4",
    },
  ),
);

if (typeof window !== "undefined") {
  const cookies = getClientCookies();

  if (cookies?.created_at && isSessionExpired(cookies.created_at)) {
    useUserStore.getState().reset();
  }
}
